import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { PdfImportingStatus } from '../constants';
import { isEditorOpened } from './utils';
import { reset } from './ui';
import { setThumbnailsToUpdate, updateThumbnailsIfNeeded } from './thumbnails';
import { getPreprodPublicationUrl } from './preprodPublication';
import * as helpers from '../utils/helpers';
import { createNotification } from '../../NotificationBar/actions';
import {
	addFileInImport,
	updateFileInImport,
	removeFileFromImport,
	FileError,
} from '../../ImportManager/actions';
import { Api } from '../../../typings/api';
import ApiUtils from '../../../utils/api';

export enum SettingsActionKeys {
	TOGGLE_SETTINGS_PANEL = '@@editor/settings/TOGGLE_SETTINGS_PANEL',
	SET_SETTINGS = '@@editor/settings/SET_SETTINGS',
	SET_SETTINGS_ERROR = '@@editor/settings/SET_SETTINGS_ERROR',
	SET_PDF_IMPORTING_STATUS = '@@editor/settings/SET_PDF_IMPORTING_STATUS',
}

interface ToggleSettingsPanelAction extends Action {
	type: SettingsActionKeys.TOGGLE_SETTINGS_PANEL;
}

interface SetSettingsAction extends Action {
	type: SettingsActionKeys.SET_SETTINGS;
	fields?: Api.PublicationSettings;
	pdfSettings?: Api.PublicationPdfSettings;
	projectUrl?: string;
}

interface SetSettingsErrorAction extends Action {
	type: SettingsActionKeys.SET_SETTINGS_ERROR;
	name: Api.PublicationSettingName;
	value: string | null;
}

interface SetPdfImportingStatusAction extends Action {
	type: SettingsActionKeys.SET_PDF_IMPORTING_STATUS;
	status: PdfImportingStatus | null;
}

export type SettingsAction =
	ToggleSettingsPanelAction |
	SetSettingsAction |
	SetSettingsErrorAction |
	SetPdfImportingStatusAction;

export const toggleSettingsPanel = (): ToggleSettingsPanelAction => ({
	type: SettingsActionKeys.TOGGLE_SETTINGS_PANEL,
});

const setPdfImportingStatus = (status: PdfImportingStatus | null): SetPdfImportingStatusAction => ({
	type: SettingsActionKeys.SET_PDF_IMPORTING_STATUS,
	status,
});

export const getPublicationSettings = (id: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			galleryInfos: { template: { publicationSettings: editablePublicationSettings } },
		} = getState();

		const promises: [
			Promise<Api.PublicationSettings>,
			Promise<Api.PublicationIntegration | undefined>,
			Promise<Api.PublicationPdfSettings | undefined>
		] = [
			ApiUtils.getPublicationSettings(id),
			Promise.resolve(undefined),
			Promise.resolve(undefined),
		];

		if (editablePublicationSettings!.includes(Api.PublicationSettingName.customUrl)) {
			promises[1] = ApiUtils.getPublicationIntegration(id);
		}

		if (editablePublicationSettings!.includes(Api.PublicationSettingName.downloadPdf)) {
			promises[2] = ApiUtils.getPublicationPdfSettings(id);
		}

		const [settings, integrationSettings, pdfSettings] = await Promise.all(promises);
		dispatch(setPublicationSettings(
			settings,
			integrationSettings ? integrationSettings.projectUrl : undefined,
			pdfSettings
		));
	} catch (error) {
		dispatch(createNotification('error.get_publication_settings'));
	}
};

export const savePublicationSettings = (
	id: number,
	values: Api.PublicationSettings,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<string | void> => {
	try {
		const action: SetSettingsAction = {
			type: SettingsActionKeys.SET_SETTINGS,
			fields: values,
		};
		dispatch(action);

		const settings = await ApiUtils.savePublicationSettings(id, values);

		if (Object.keys(values).includes(Api.PublicationSettingName.customUrl)) {
			const { projectUrl } = await ApiUtils.getPublicationIntegration(id);
			dispatch(setPublicationSettings(settings, projectUrl));

			const { clientId, originalGId, tempGId } = helpers.getClientAndResourceIds();
			const isNotPublished = originalGId === tempGId;

			if (isNotPublished) {
				const { editor: { label: { label } } } = getState();
				dispatch(getPreprodPublicationUrl(clientId, tempGId, label, true));
			}

			return projectUrl;
		} else {
			dispatch(setPublicationSettings(settings));
		}
	} catch (error) {
		let errorMessage = 'error.save_publication_settings';

		if (error.responseText && error.responseText.includes('NOT_UNIQUE_ERROR--customUrl')) {
			errorMessage = 'error.custom_url_used';
			dispatch(setSettingsError(Api.PublicationSettingName.customUrl, errorMessage));
		}

		dispatch(createNotification(errorMessage));
	}
};

export const setPublicationSettings = (
	settings: Api.PublicationSettings,
	projectUrl?: string,
	pdfSettings?: Api.PublicationPdfSettings,
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { galleryInfos: { template: { publicationSettings } } } = getState();
	const fields = publicationSettings!.reduce((acc, current) => {
		if (settings.hasOwnProperty(current)) {
			acc[current] = settings[current];
		}
		return acc;
	}, {} as Api.PublicationSettings);

	const action: SetSettingsAction = {
		type: SettingsActionKeys.SET_SETTINGS,
		fields,
		pdfSettings,
		projectUrl,
	};
	dispatch(action);
};

export const setSettingsError = (name: Api.PublicationSettingName, value: string | null) => (dispatch: Dispatch<StoreState>) => {
	const action: SetSettingsErrorAction = {
		type: SettingsActionKeys.SET_SETTINGS_ERROR,
		name,
		value,
	};
	dispatch(action);
};

export const savePublicationPdfOption = (id: number, pdfOption: Api.PublicationPdfOption) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const pdfSettings = await ApiUtils.savePublicationPdfOption(id, pdfOption);
		const action: SetSettingsAction = {
			type: SettingsActionKeys.SET_SETTINGS,
			pdfSettings,
		};
		dispatch(action);
	} catch (error) {
		dispatch(createNotification('error.save_publication_settings'));
	}
};

export const importPublicationPdf = (
	publicationId: number,
	importManagerId: string,
	sourceFile: { file: File, isCorrectFileType: boolean } | null,
	sourcePublication: { id: number, label: string } | null,
	updateThumbs: boolean,
	pdfOption: Api.PublicationPdfOption,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const isRemovingPublication = !!sourcePublication && sourcePublication.id === -1;
		const pdfImportingStatus = isRemovingPublication
			? PdfImportingStatus.REMOVING
			: PdfImportingStatus.IMPORTING;
		dispatch(setPdfImportingStatus(pdfImportingStatus));

		let response: Api.PublicationPdfImportResponse | void;

		if (sourceFile) {
			response = await dispatch(importPdfFromFile(publicationId, importManagerId, sourceFile, updateThumbs));
		}

		if (sourcePublication) {
			response = await dispatch(importPdfFromPublication(publicationId, sourcePublication, updateThumbs));
		}

		if (!response) {
			dispatch(setPdfImportingStatus(null));
			return;
		}

		if (!isEditorOpened()) {
			const notification = isRemovingPublication
				? 'info.remove_publication_pdf'
				: 'info.import_publication_pdf';
			dispatch(createNotification(notification));
			return;
		}

		await dispatch(updatePdfSettings(publicationId, sourceFile, isRemovingPublication, pdfOption));
		dispatch(setPdfImportingStatus(null));
		await dispatch(handlePublicationPdfImportResponse(response, updateThumbs, publicationId));
	} catch (error) {
		dispatch(setPdfImportingStatus(null));
		dispatch(createNotification('error.import_publication_pdf'));
	}
};

const importPdfFromFile = (
	publicationId: number,
	importManagerId: string,
	sourceFile: { file: File, isCorrectFileType: boolean },
	updateThumbs: boolean,
) => async (dispatch: Dispatch<StoreState>): Promise<Api.PublicationPdfImportResponse | void> => {
	try {
		const { file, isCorrectFileType } = sourceFile;

		if (!isCorrectFileType) {
			dispatch(updateFileInImport(importManagerId, {
				error: FileError.TYPE,
				percentage: 100,
			}));
			return;
		}

		dispatch(addFileInImport(importManagerId, {
			id: importManagerId,
			percentage: 0,
			error: null,
		}));

		const onImportProgress = (e: ProgressEvent) => {
			const percentage = Math.floor((e.loaded / e.total) * 100);
			dispatch(updateFileInImport(importManagerId, {
				percentage,
			}));
		};

		const response = await ApiUtils.importPublicationPdf(publicationId, file, null, updateThumbs, onImportProgress, importManagerId);

		dispatch(removeFileFromImport(importManagerId));

		return response;
	} catch (error) {
		dispatch(updateFileInImport(importManagerId, {
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

const importPdfFromPublication = (
	publicationId: number,
	sourcePublication: { id: number, label: string },
	updateThumbs: boolean,
) => async (dispatch: Dispatch<StoreState>): Promise<Api.PublicationPdfImportResponse | void> => {
	try {
		return await ApiUtils.importPublicationPdf(publicationId, null, sourcePublication, updateThumbs);
	} catch (error) {
		dispatch(createNotification('error.import_publication_pdf'));
	}
};

const updatePdfSettings = (
	publicationId: number,
	sourceFile: { file: File, isCorrectFileType: boolean } | null,
	isRemovingPublication: boolean,
	pdfOption: Api.PublicationPdfOption,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	const pdfSettings = await ApiUtils.savePublicationPdfOption(publicationId, pdfOption);

	if (sourceFile) {
		pdfSettings.globalId = undefined;
	}

	if (isRemovingPublication) {
		pdfSettings.globalId = undefined;
		pdfSettings.label = pdfSettings.label || undefined;
	}

	const action: SetSettingsAction = {
		type: SettingsActionKeys.SET_SETTINGS,
		pdfSettings,
	};
	dispatch(action);
};

const handlePublicationPdfImportResponse = (
	response: Api.PublicationPdfImportResponse,
	updateThumbs: boolean,
	publicationId: number,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	if (response === Api.PublicationPdfImportResponse.SYNC && updateThumbs) {
		dispatch(updateThumbnailsAfterImportPdf());
		return;
	}

	if (response === Api.PublicationPdfImportResponse.ASYNC) {
		dispatch(setPdfImportingStatus(PdfImportingStatus.PROCESSING));

		const importManagerId = helpers.getPublicationPdfImportingId(publicationId, PdfImportingStatus.PROCESSING);
		dispatch(addFileInImport(importManagerId, {
			id: importManagerId,
			percentage: 0,
			error: null,
		}));

		const onProgressUpdate = (percentage: number) => {
			dispatch(updateFileInImport(importManagerId, {
				percentage,
			}));
		};

		const onProgressError = () => {
			dispatch(setPdfImportingStatus(null));
			dispatch(removeFileFromImport(importManagerId));
			dispatch(createNotification('error.import_publication_pdf'));
		};

		const onProgressFinish = () => {
			dispatch(setPdfImportingStatus(null));
			dispatch(removeFileFromImport(importManagerId));

			if (updateThumbs) {
				dispatch(updateThumbnailsAfterImportPdf());
			}
		};

		const shouldCancelProgress = (state: StoreState) => {
			return !isEditorOpened(state);
		};

		const onProgressCancel = () => {
			dispatch(removeFileFromImport(importManagerId));
			dispatch(reset(true));
		};

		const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
		await ApiUtils.getPublicationProgress(clientId, resourceId, onProgressUpdate, onProgressError, onProgressFinish, shouldCancelProgress, onProgressCancel);
	}
};

const updateThumbnailsAfterImportPdf = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const { editor: { pagesController: { totalPages } } } = getState();
	const positionsToUpdate = Array.from({ length: totalPages }, (v, i) => i + 1);
	dispatch(setThumbnailsToUpdate(positionsToUpdate));
	const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
	dispatch(updateThumbnailsIfNeeded(clientId, resourceId));
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/settings.ts